import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback } from "react";
import { TAG_DELIMITER, useTagBox } from "./TagBox";

const MAX_SUGGESTIONS = 30;

export const useTagSuggest = () => {
  const { tags } = useAppContext();
  const { value, setSuggestions, mode, setIDX, setOpen } = useTagBox();

  const suggest = useCallback(
    (query: string) => {
      // Return the top 30 tags by default if there are no query yet
      if (!query) {
        console.log(`suggesting ${query}`);

        setSuggestions(Array.from(new Set(tags)).slice(0, MAX_SUGGESTIONS));
        setIDX(0);
        setOpen(true);
        return;
      }

      const existingTags =
        mode === "combo"
          ? [value]
          : value
              .split(/[\s\n]/)
              .filter((word) => word.startsWith(TAG_DELIMITER))
              .map((tag) => tag.slice(TAG_DELIMITER.length));

      const scoredSuggestions = tags
        .filter((suggestion) => !existingTags.includes(suggestion))
        .map((suggestion) => ({
          text: suggestion,
          score: calculateSimilarity(query, suggestion)
        }))
        .filter((item) => item.score > 0.6)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.text.localeCompare(b.text);
        });

      const uniqueSuggestions = Array.from(
        new Map(scoredSuggestions.map((item) => [item.text.toLowerCase(), item])).values()
      );

      setSuggestions(uniqueSuggestions.slice(0, MAX_SUGGESTIONS).map((item) => item.text));
      setIDX(0);
      setOpen(true);
    },

    [mode, setIDX, setOpen, setSuggestions, tags, value]
  );

  return suggest;
};

/**
 * Calculates the Levenshtein distance between two strings.
 *
 * Defined as the minimum number of single-character edits (insertions, deletions or substitutions)
 * required to change one string into the other.
 *
 * @param str1 - The first string to compare.
 * @param str2 - The second string to compare.
 * @returns The Levenshtein distance between the two strings.
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const track = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i += 1) track[0][i] = i;
  for (let j = 0; j <= str2.length; j += 1) track[j][0] = j;

  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(track[j][i - 1] + 1, track[j - 1][i] + 1, track[j - 1][i - 1] + indicator);
    }
  }
  return track[str2.length][str1.length];
};

/**
 * Calculates the similarity between a query string and a suggestion string.
 *
 * @param query - The query string to compare against.
 * @param suggestion - The suggestion string to compare.
 * @returns The similarity score between the query and suggestion, ranging from 0 to 1.
 */
const calculateSimilarity = (query: string, suggestion: string): number => {
  const queryLower = query.toLowerCase();
  const suggestionLower = suggestion.toLowerCase();

  if (suggestionLower.startsWith(queryLower)) return 1;
  if (suggestionLower.includes(queryLower)) return 0.8;

  const maxLength = Math.max(queryLower.length, suggestionLower.length);
  const distance = levenshteinDistance(queryLower, suggestionLower);
  return 1 - distance / maxLength;
};
